import { addDays, toLocalDate, type LocalDate } from './date';
import {
  ACTIVITY_DAYS,
  HISTORY_LIMIT,
  type AnswerResult,
  type Box,
  type CardRecord,
  type HistoryEntry,
  type ProgressState,
} from './types';

/** 箱ごとの間隔（日。progress-store.md §4.2）。 */
export const INTERVAL: Record<Box, number> = { 1: 1, 2: 2, 3: 4, 4: 8, 5: 16 };

export interface Answer {
  date: LocalDate;
  result: AnswerResult;
  hint: boolean;
}

function toBox(n: number): Box {
  if (n <= 1) return 1;
  if (n === 2) return 2;
  if (n === 3) return 3;
  if (n === 4) return 4;
  return 5;
}

/** 新しい箱（§4.2）。未回答は 0 として扱う。 */
export function nextBox(box: number, result: AnswerResult, hint: boolean): Box {
  if (result === 'wrong') return 1;
  if (hint) return toBox(Math.max(box, 1));
  return toBox(Math.min(box + 1, 5));
}

/**
 * 1 回の回答をカードの記録に当てる（§4.1, §4.2。ベクタ L01〜L10）。
 * その日の最初の回答だけが箱を動かす（evaluated: true）。2 回目以降は seen / correct だけ。
 */
export function applyAnswer(
  before: CardRecord | null | undefined,
  answer: Answer,
): { record: CardRecord; evaluated: boolean } {
  const correctInc = answer.result === 'correct' ? 1 : 0;
  if (before && before.lastEvaluated === answer.date) {
    return {
      record: { ...before, seen: before.seen + 1, correct: before.correct + correctInc },
      evaluated: false,
    };
  }
  const box = nextBox(before?.box ?? 0, answer.result, answer.hint);
  return {
    record: {
      box,
      due: addDays(answer.date, INTERVAL[box]),
      firstSeen: before?.firstSeen ?? answer.date,
      lastEvaluated: answer.date,
      seen: (before?.seen ?? 0) + 1,
      correct: (before?.correct ?? 0) + correctInc,
    },
    evaluated: true,
  };
}

export interface AnswerInput {
  cardId: string;
  result: AnswerResult;
  hint: boolean;
  verdict?: 'AC' | 'WA' | 'RE' | 'TLE';
  preflightMatched?: boolean;
}

/** activity を直近 {@link ACTIVITY_DAYS} 日だけにする（§3）。 */
export function pruneActivity(
  activity: Record<LocalDate, number>,
  today: LocalDate,
): Record<LocalDate, number> {
  const oldest = addDays(today, -(ACTIVITY_DAYS - 1));
  const out: Record<LocalDate, number> = {};
  for (const [d, n] of Object.entries(activity)) if (d >= oldest) out[d] = n;
  return out;
}

/**
 * 回答を状態に記録する（§3, §4）。cards / activity / history を更新した新しい状態を返す。
 * 回答として数えないもの（§4.3。CE、sandboxd エラー、invalid）は呼び出し側で除く。
 */
export function recordAnswer(
  state: ProgressState,
  input: AnswerInput,
  at: Date,
): { state: ProgressState; evaluated: boolean; record: CardRecord } {
  const date = toLocalDate(at);
  const { record, evaluated } = applyAnswer(state.cards[input.cardId], {
    date,
    result: input.result,
    hint: input.hint,
  });
  const entry: HistoryEntry = {
    at: at.toISOString(),
    date,
    cardId: input.cardId,
    result: input.result,
    hint: input.hint,
    evaluated,
  };
  if (input.verdict !== undefined) entry.verdict = input.verdict;
  if (input.preflightMatched !== undefined) entry.preflightMatched = input.preflightMatched;
  const activity = pruneActivity(
    { ...state.activity, [date]: (state.activity[date] ?? 0) + 1 },
    date,
  );
  return {
    state: {
      version: 1,
      cards: { ...state.cards, [input.cardId]: record },
      activity,
      history: [entry, ...state.history].slice(0, HISTORY_LIMIT),
    },
    evaluated,
    record,
  };
}
